import { useState, useEffect } from 'react';
import axios from 'axios';
import { Megaphone } from 'lucide-react';

const API = '/api';

/**
 * A dated suggestion from a rollout plan, shown where the issue is actually composed.
 *
 * SUGGESTS, NEVER INSERTS. Nothing here writes into an issue. Whether the line goes in is an
 * editorial call in the composer, and a note that made it would be scheduling content by
 * side effect - the same rule VideoCandidatesNote follows.
 *
 * DERIVED, NOT TYPED HERE. The dates and the words come from the backend's rollout plan
 * (`ol_trusty_rollout`), the same source the social drafts and the newsletter issues carry
 * their dates from - so this note and those drafts can never name two different Wednesdays.
 * The backend drops a note once its date is past, so the note goes quiet by itself.
 *
 * Props:
 *   campaignId: the rollout, e.g. 'ol-trusty-launch'
 *   where: which composer this is, e.g. 'weekly-fitter'
 */
export default function RolloutNote({ campaignId = 'ol-trusty-launch', where = 'weekly-fitter' }) {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/rollouts/${encodeURIComponent(campaignId)}/notes`, { params: { where } })
      .then((res) => { if (live) setNotes(res.data.notes || []); })
      // Additive. If the lookup fails the composer is unaffected, and no note is not a
      // claim that there is no plan - it is simply no note.
      .catch(() => {});
    return () => { live = false; };
  }, [campaignId, where]);

  if (!notes.length) return null;

  const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div
      data-testid="rollout-note"
      className="flex items-start gap-2 text-[11px] font-sans text-hg-text-dim bg-hg-surface border border-hg-border rounded-sm px-3 py-2"
    >
      <Megaphone size={13} className="shrink-0 mt-0.5 text-hg-green" />
      <div className="space-y-1">
        {notes.map((n) => (
          <div key={`${n.issue_date}-${n.title}`}>
            <span className="text-hg-text">{fmt(n.issue_date)} · {n.title}</span>
            {' — '}{n.suggestion}
          </div>
        ))}
      </div>
    </div>
  );
}
